// apps/web/src/components/SpecialTariffs.tsx
import React from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { HelpTooltip } from "../shared/ui/help/HelpTooltip";

type BreakdownChipProps = {
  label: string;
  tooltip?: string;
};

function BreakdownChip({ label, tooltip }: BreakdownChipProps) {
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#EDF2F7",
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 900,
          color: "#4A5568",
          lineHeight: 1,
          flexShrink: 0,
          userSelect: "none",
          mt: "-1px",
        }}
      >
        +
      </Typography>

      <Typography
        sx={{
          fontSize: "0.8rem",
          color: "#2D3748",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </Typography>

      {tooltip ? (
        <HelpTooltip title={tooltip} arrow>
          <IconButton
            size="small"
            sx={{
              p: 0,
              color: "#6A7A89",
              flexShrink: 0,
            }}
            aria-label="info"
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </HelpTooltip>
      ) : null}
    </Box>
  );
}

function TariffCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: "1px solid #E2E8F0",
        borderRadius: 2,
        bgcolor: "#F7FAFC",
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

export function SpecialTariffs() {
  const { t } = useTranslation();

  const operatorTooltip = t(
    "onboarding.step5.specialTariffs.tooltips.operator"
  );
  const taxesTooltip = t("onboarding.step5.specialTariffs.tooltips.taxes");

  return (
    <Stack spacing={2}>
      {/* Card 1: Atlante Fast chargers (DC) */}
      <TariffCard>
        <Typography
          sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1A1A1A" }}
        >
          {t("onboarding.step5.specialTariffs.cards.fast.title")}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#718096", mt: 0.25 }}>
          {t("onboarding.step5.specialTariffs.cards.fast.power")}
        </Typography>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography
            sx={{ fontSize: "2rem", fontWeight: 800, color: "#1A1A1A" }}
          >
            {t("onboarding.step5.specialTariffs.cards.fast.price")}
            <Typography
              component="span"
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1A1A1A" }}
            >
              {" "}
              {t("onboarding.step5.specialTariffs.cards.fast.unit")}
            </Typography>
          </Typography>

          <Typography sx={{ fontSize: "0.78rem", color: "#718096", mt: 0.5 }}>
            {t("onboarding.step5.specialTariffs.cards.fast.note")}
          </Typography>
        </Box>
      </TariffCard>

      {/* Card 2: Fast chargers of other operators (DC) */}
      <TariffCard>
        <Typography
          sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1A1A1A" }}
        >
          {t("onboarding.step5.specialTariffs.cards.otherFast.title")}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#718096", mt: 0.25 }}>
          {t("onboarding.step5.specialTariffs.cards.otherFast.power")}
        </Typography>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography
            sx={{ fontSize: "2rem", fontWeight: 800, color: "#1A1A1A" }}
          >
            {t("onboarding.step5.specialTariffs.cards.otherFast.price")}
            <Typography
              component="span"
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1A1A1A" }}
            >
              {" "}
              {t("onboarding.step5.specialTariffs.cards.otherFast.unit")}
            </Typography>
          </Typography>

          <Typography sx={{ fontSize: "0.78rem", color: "#718096", mt: 0.5 }}>
            {t("onboarding.step5.specialTariffs.cards.otherFast.note")}
          </Typography>
        </Box>
      </TariffCard>

      {/* Card 3: Slow chargers (AC) — Atlante and other operators */}
      <TariffCard>
        <Typography
          sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1A1A1A" }}
        >
          {t("onboarding.step5.specialTariffs.cards.slow.title")}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#718096", mt: 0.25 }}>
          {t("onboarding.step5.specialTariffs.cards.slow.power")}
        </Typography>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "1rem", fontWeight: 700, color: "#1A1A1A" }}
          >
            {t("onboarding.step5.specialTariffs.cards.slow.energyLabel")}
          </Typography>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: "1.35rem",
                fontWeight: 800,
                color: "#1A1A1A",
                lineHeight: 1.1,
              }}
            >
              {t("onboarding.step5.specialTariffs.cards.slow.price")}
              <Typography
                component="span"
                sx={{ fontSize: "0.95rem", fontWeight: 700 }}
              >
                {" "}
                {t("onboarding.step5.specialTariffs.cards.slow.unit")}
              </Typography>
            </Typography>
            <Typography
              sx={{ fontSize: "0.78rem", color: "#718096", mt: 0.25 }}
            >
              {t("onboarding.step5.specialTariffs.cards.slow.vatNote")}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          sx={{ mt: 2 }}
        >
          <BreakdownChip
            label={t(
              "onboarding.step5.specialTariffs.cards.slow.breakdown.operator"
            )}
            tooltip={operatorTooltip}
          />
          <BreakdownChip
            label={t(
              "onboarding.step5.specialTariffs.cards.slow.breakdown.taxes"
            )}
            tooltip={taxesTooltip}
          />
        </Stack>
      </TariffCard>
    </Stack>
  );
}
